import { useState } from "react";
import { styled } from "@mui/material/styles";
import { Box, Stack, Typography, IconButton, Tooltip } from "@mui/material";
import {
  BusinessOutlined as InsurersIcon,
  GridView as OverviewIcon,
  FeedOutlined as ContractsIcon,
  List as ClaimsIcon,
  HelpOutline as HelpIcon,
  Headset as ContactIcon,
  MapsHomeWorkOutlined as ObjectsIcon,
  MenuOpenOutlined as MenuOpenIcon,
  NoteAltOutlined as ControllingIcon,
  PeopleOutline as UsersIcon,
  EmailOutlined as EmailIcon,
  WbSunnyOutlined as SubstitutionsIcon,
  WorkOutlineOutlined as CustomersIcon,
  NotificationsActiveOutlined as ReminderIcon,
  Euro as AccountingIcon,
} from "@mui/icons-material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { useAbility } from "../../hooks/permissions/useAbility.jsx";
import { useInsurerPermissions } from "../../hooks/permissions/useInsurerPermissions.jsx";
import { useUser } from "../../hooks/user.js";
import { logout } from "../../services/authService.js";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import ContactModal from "../../components/modals/ContactModal.jsx";
import { activeBrand } from "../../theme/brands/index.js";
import componentTokens from "../../theme/tokens/components.js";
import { colorsFoundation } from "../../theme/tokens/colors-foundation.js";

const sidebarTokens = componentTokens.sidebar;

const SidebarContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  height: "100%",
  padding: "20px 0 12px",
  backgroundColor: theme.palette.background.sidebar,
  transition: "all 0.3s ease",
}));

// Logo + product name sit on one row (dummy: coloured mark then white caption).
const LogoSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ isMinimized }) => ({
  display: "flex",
  flexDirection: isMinimized ? "column" : "row",
  alignItems: "center",
  gap: sidebarTokens.item.gap,
  padding: isMinimized ? "0 0 20px" : "0 16px 24px",
  justifyContent: isMinimized ? "center" : "flex-start",
}));

const LogoBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(() => ({
  width: "32px",
  height: "32px",
  flex: "none",
  borderRadius: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  "& img": {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
}));

const TitleRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ isMinimized }) => ({
  display: isMinimized ? "none" : "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flex: 1,
  minWidth: 0,
}));

const SidebarTitle = styled(Typography)(() => ({
  fontSize: "15px",
  fontWeight: 600,
  lineHeight: 1.5,
  letterSpacing: "-0.2px",
  color: sidebarTokens.title,
}));

const NavSection = styled(Stack)(() => ({
  gap: "2px",
  width: "100%",
}));

const NavItem = styled(RouterLink, {
  shouldForwardProp: (prop) =>
    prop !== "active" &&
    prop !== "isMinimized" &&
    prop !== "toolTip" &&
    prop !== "toolTipPlacement" &&
    prop !== "toolTipTitle",
})(({ active, isMinimized }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isMinimized ? "center" : "flex-start",
  gap: sidebarTokens.item.gap,
  padding: isMinimized
    ? sidebarTokens.item.paddingY
    : `${sidebarTokens.item.paddingY} ${sidebarTokens.item.paddingX}`,
  margin: `0 ${sidebarTokens.item.marginX}`,
  borderRadius: sidebarTokens.item.borderRadius,
  backgroundColor: active ? sidebarTokens.item.activeBg : "transparent",
  textDecoration: "none",
  cursor: "pointer",
  transition: "background 0.15s ease",
  "&:hover": {
    backgroundColor: active
      ? sidebarTokens.item.activeBg
      : sidebarTokens.item.hoverBg,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
    color: active ? sidebarTokens.item.activeIcon : sidebarTokens.item.icon,
  },
  "& .MuiTypography-root": {
    fontSize: sidebarTokens.item.fontSize,
    fontWeight: active ? 600 : 400,
    color: active ? sidebarTokens.item.activeText : sidebarTokens.item.text,
  },
}));

function SidebarNavItem({
  children,
  toolTip,
  toolTipPlacement,
  toolTipTitle,
  isVisible = true,
  ...props
}) {
  if (isVisible === false) {
    return null;
  }

  return (
    <NavItem {...props}>
      {toolTip ? (
        <Tooltip title={toolTipTitle} placement={toolTipPlacement}>
          {children[0]}
        </Tooltip>
      ) : (
        children[0]
      )}
      {children[1]}
    </NavItem>
  );
}

SidebarNavItem.propTypes = {
  children: PropTypes.node.isRequired,
  toolTip: PropTypes.bool,
  toolTipPlacement: PropTypes.string,
  toolTipTitle: PropTypes.string,
  isVisible: PropTypes.bool,
};

const NavText = styled(Typography)({
  lineHeight: 1.5,
  whiteSpace: "nowrap",
});

const Spacer = styled(Box)({
  flex: 1,
  minHeight: "1px",
});

const BottomSection = styled(Stack)({
  gap: 0,
});

const BottomLink = styled(RouterLink, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ isMinimized }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: isMinimized ? "center" : "flex-start",
  gap: sidebarTokens.item.gap,
  padding: isMinimized
    ? sidebarTokens.item.paddingY
    : `${sidebarTokens.item.paddingY} ${sidebarTokens.item.paddingX}`,
  margin: `0 ${sidebarTokens.item.marginX}`,
  borderRadius: sidebarTokens.item.borderRadius,
  textDecoration: "none",
  cursor: "pointer",
  transition: "background 0.15s ease",
  "&:hover": {
    backgroundColor: sidebarTokens.item.hoverBg,
  },
  "& .MuiSvgIcon-root": {
    fontSize: "18px",
    color: sidebarTokens.item.icon,
  },
  "& .MuiTypography-root": {
    fontSize: sidebarTokens.item.fontSize,
    fontWeight: 400,
    color: sidebarTokens.item.text,
  },
}));

// User display at the bottom of the sidebar (dummy: avatar + name + Abmelden).
const SidebarUser = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMinimized",
})(({ isMinimized }) => ({
  margin: isMinimized ? "10px 4px 4px" : "10px 8px 4px",
  padding: isMinimized ? "6px" : "12px",
  borderRadius: "8px",
  backgroundColor: "rgba(255,255,255,0.06)",
  display: "flex",
  alignItems: "center",
  justifyContent: isMinimized ? "center" : "flex-start",
  gap: "10px",
  cursor: "pointer",
  transition: "background 0.15s ease",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
}));

const SidebarUserAvatar = styled(Box)(() => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  background: colorsFoundation.gray[700],
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "none",
  "& span": {
    color: colorsFoundation.neutral.white,
    fontSize: "11px",
    fontWeight: 600,
  },
}));

function getInitials(name) {
  if (!name) return "U";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 0) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default function Sidebar({ logoSrc, onToggle }) {
  const navigate = useNavigate();
  const { user } = useUser();
  const location = useLocation();
  const { can } = useAbility();
  const { canViewAny: canViewAnyInsurer } = useInsurerPermissions();
  const [isMinimized, setIsMinimized] = useState(false);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [contactOpen, setContactOpen] = useState(false);

  // Skin-level visibility: a brand may hide a nav item regardless of role.
  // `undefined`/`true` = shown, `false` = hidden. Combined with permissions.
  const navFeatures = activeBrand.features?.nav ?? {};
  const navEnabled = (key) => navFeatures[key] !== false;

  // Skinning: help button + which user display to show ("header"|"sidebar"|"both").
  const showHelp = activeBrand.features?.help !== false;
  const userDisplay = activeBrand.features?.userDisplay ?? "both";
  const showSidebarUser = userDisplay === "sidebar" || userDisplay === "both";

  const userName =
    String(`${user?.vorname ?? ""} ${user?.nachname ?? ""}`).trim() || "Benutzer";
  const userInitials = getInitials(userName);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const isActive = (path) => {
    return path.length > 1
      ? location.pathname.startsWith(path)
      : location.pathname === path;
  };

  const handleToggle = () => {
    setIsMinimized(!isMinimized);
    if (onToggle) {
      onToggle(!isMinimized);
    }
  };

  return (
    <SidebarContainer isMinimized={isMinimized}>
      <LogoSection isMinimized={isMinimized}>
        <LogoBox isMinimized={isMinimized}>
          {logoSrc && <img src={logoSrc} alt={activeBrand.assets.logoAlt} />}
        </LogoBox>
        <TitleRow isMinimized={isMinimized}>
          {activeBrand.features?.showSidebarTitle !== false && (
            <SidebarTitle>{activeBrand.productName}</SidebarTitle>
          )}
        </TitleRow>
        {isDesktop && (
          <IconButton
            size="small"
            onClick={handleToggle}
            sx={{
              color: sidebarTokens.item.icon,
              padding: 0.5,
              flex: "none",
              transform: isMinimized ? "scaleX(-1)" : "none",
              transition: "transform 0.3s ease",
              "&:hover": { color: sidebarTokens.title },
            }}
          >
            <MenuOpenIcon fontSize="small" />
          </IconButton>
        )}
      </LogoSection>

      <NavSection>
        <SidebarNavItem
          to="/home"
          active={isActive("/home")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Übersicht"
        >
          <OverviewIcon />
          {!isMinimized && <NavText>Übersicht</NavText>}
        </SidebarNavItem>
        <SidebarNavItem
          to="/schaeden"
          active={isActive("/schaeden")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Schäden"
          isVisible={can("Claim", "viewAny") && navEnabled("claims")}
        >
          <ClaimsIcon />
          {!isMinimized && <NavText>Schäden</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/admin/kunden"
          active={isActive("/admin/kunden")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Kunden"
          isVisible={can("Customer", "viewAny") && navEnabled("customers")}
        >
          <CustomersIcon />
          {!isMinimized && <NavText>Kunden</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/admin/insurers"
          active={isActive("/admin/insurers")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Versicherungsgesellschaften"
          isVisible={canViewAnyInsurer() && navEnabled("insurers")}
        >
          <InsurersIcon />
          {!isMinimized && <NavText>Versicherer</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/admin/vertraege"
          active={isActive("/admin/vertraege")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Verträge"
          isVisible={can("Contract", "viewAny") && navEnabled("contracts")}
        >
          <ContractsIcon />
          {!isMinimized && <NavText>Verträge</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/admin/objekte"
          active={isActive("/admin/objekte")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Objekte"
          isVisible={can("Object", "viewAny") && navEnabled("objects")}
        >
          <ObjectsIcon />
          {!isMinimized && <NavText>Objekte</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/controlling"
          active={isActive("/controlling")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Controlling"
          isVisible={can("Controlling", "viewAny") && navEnabled("controlling")}
        >
          <ControllingIcon />
          {!isMinimized && <NavText>Controlling</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/admin/users"
          active={isActive("/admin/users")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Benutzer"
          isVisible={can("User", "viewAny") && navEnabled("users")}
        >
          <UsersIcon />
          {!isMinimized && <NavText>Benutzer</NavText>}
        </SidebarNavItem>
        <SidebarNavItem
          to="/admin/emails"
          active={isActive("/admin/emails")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Posteingang – eingehende und ausgehende E-Mails"
          isVisible={can("Mail", "viewAny") && navEnabled("mail")}
        >
          <EmailIcon />
          {!isMinimized && <NavText>Posteingang</NavText>}
        </SidebarNavItem>
        <SidebarNavItem
          to="/substitutions"
          active={isActive("/substitutions")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Stellvertretungen"
          isVisible={can("Substitution", "viewAny") && navEnabled("substitutions")}
        >
          <SubstitutionsIcon />
          {!isMinimized && <NavText>Stellvertretungen</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/admin/reminders"
          active={isActive("/admin/reminders")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Schadenserinnerungen"
          isVisible={can("ClaimStatusReminder", "viewAny") && navEnabled("reminders")}
        >
          <ReminderIcon />
          {!isMinimized && <NavText>Erinnerungen</NavText>}
        </SidebarNavItem>

        <SidebarNavItem
          to="/admin/buchungen"
          active={isActive("/admin/buchungen")}
          isMinimized={isMinimized}
          toolTip={isMinimized}
          toolTipPlacement="right"
          toolTipTitle="Buchungsverwaltung"
          isVisible={can("booking", "viewAny") && navEnabled("bookings")}
        >
          <AccountingIcon />
          {!isMinimized && <NavText>Buchungen</NavText>}
        </SidebarNavItem>
      </NavSection>

      <Spacer />

      <BottomSection>
        <BottomLink
          to="#"
          isMinimized={isMinimized}
          onClick={(e) => {
            e.preventDefault();
            setContactOpen(true);
          }}
        >
          <ContactIcon />
          {!isMinimized && <NavText>Kontakt</NavText>}
        </BottomLink>
        {showHelp && (
          <BottomLink
            to="#"
            isMinimized={isMinimized}
            onClick={(e) => e.preventDefault()}
          >
            <HelpIcon />
            {!isMinimized && <NavText>Hilfe</NavText>}
          </BottomLink>
        )}
        {showSidebarUser && (
          <SidebarUser
            isMinimized={isMinimized}
            onClick={handleLogout}
            title="Abmelden"
          >
            <SidebarUserAvatar>
              <span>{userInitials}</span>
            </SidebarUserAvatar>
            {!isMinimized && (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: colorsFoundation.neutral.white,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {userName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: colorsFoundation.gray[500],
                    mt: "2px",
                  }}
                >
                  Abmelden
                </Typography>
              </Box>
            )}
          </SidebarUser>
        )}
      </BottomSection>

      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </SidebarContainer>
  );
}

Sidebar.propTypes = {
  logoSrc: PropTypes.string,
  onToggle: PropTypes.func,
};
